import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  LoadingOverlay,
  Drawer as MantineDrawer,
  Tabs as MantineTabs,
  Menu,
  px,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import { ClassWorkoutView } from 'components/Workout';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useClass from 'hooks/class/useClass';
import useUpdateClass from 'hooks/class/useUpdateClass';
import useProgramCalendar from 'hooks/program/useProgramCalendar';
import EditProgramForm from 'pages/private/owner/Program/components/EditProgramForm';
import { calculateEventEndDateTime } from 'utils/event.helpers';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';

import DetailsEditAllFutureClassesForm from './components/DetailsEditAllFutureClassesForm';
import DetailsEditSelectedDayForm from './components/DetailsEditSelectedDayForm';
import DetailsView from './components/DetailsView';
import ModalClassDelete from './components/ModalClassDelete';
import ReservationsForm from './components/ReservationsForm';
import { DragPrefill } from '../../hooks/useEventDragUpdate';
import Footer from '../Footer';
import NotesView from '../NotesView';
import { EventDrawerState } from '../types';

interface IEditClassDrawerProps {
  opened: boolean;
  closeHandler: () => void;
  setKiloClassId: React.Dispatch<React.SetStateAction<string | undefined>>;
  kiloClassId?: string;
  permission?: boolean;
  dragPrefill?: DragPrefill;
}

const DEFAULT_ACTIVE_TAB = 'reservations';

const EditClassDrawer = ({
  opened,
  closeHandler,
  setKiloClassId,
  kiloClassId,
  permission = true,
  dragPrefill,
}: IEditClassDrawerProps): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const workoutTrackerEnabled = isWorkoutTrackerEnabled(session?.gym?.workoutTrackerEnabled);

  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, true);
  const [activeTab, setActiveTab] = useState<string | null>(dragPrefill ? 'details' : DEFAULT_ACTIVE_TAB);

  const [state, setState] = useState<EventDrawerState>(dragPrefill ? 'edit-all-future-events' : 'view');
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const updateMutation = useUpdateClass();

  const {
    data: classData,
    isLoading: isClassDataLoading,
    isFetching: isClassDataFetching,
    isSuccess: isClassDataSuccess,
    isError: isClassDataError,
  } = useClass(kiloClassId);

  const isFetchProgramEnabled = activeTab === 'programming';

  const {
    data: programData,
    isLoading: isProgramDataLoading,
    isError: isProgramError,
    isSuccess: isProgramDataSuccess,
    refetch: refetchProgramData,
  } = useProgramCalendar({
    start: classData?.data.startDate,
    end: classData?.data.startDate,
    kiloClassTypes: classData ? [classData.data.kiloClassType.kiloClassTypeId] : [],
    // flag-ON also needs this for the read-only Legacy Programming notes, so fetch in both states.
    enabled: isFetchProgramEnabled,
  });

  const loader = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </div>
    ),
    [],
  );

  const handleCloseAndStateReset = useCallback((): void => {
    closeHandler();
    setState('view');
  }, [closeHandler]);

  const handleProgrammingClose = useCallback((): void => {
    setActiveTab(DEFAULT_ACTIVE_TAB);
    refetchProgramData();
  }, [refetchProgramData]);

  const detailsComponent = useMemo(() => {
    switch (state) {
      case 'view':
        return classData ? <DetailsView kiloClass={classData.data} /> : loader;
      case 'edit-selected-day':
        return classData ? (
          <DetailsEditSelectedDayForm
            kiloClass={classData.data}
            closeHandler={handleCloseAndStateReset}
            formId={`${state}-class-form`}
            updateMutation={updateMutation}
          />
        ) : (
          loader
        );
      case 'edit-all-future-events':
        return classData ? (
          <DetailsEditAllFutureClassesForm
            kiloClass={classData.data}
            closeHandler={handleCloseAndStateReset}
            formId={`${state}-class-form`}
            updateMutation={updateMutation}
            prefillStartTime={dragPrefill?.startTime}
            prefillOriginalCoachId={dragPrefill?.originalCoachId}
            prefillNewCoachId={dragPrefill?.newCoachId}
            prefillNewCoachName={dragPrefill?.newCoachName}
          />
        ) : (
          loader
        );
      default:
        return <></>;
    }
  }, [
    state,
    classData,
    loader,
    handleCloseAndStateReset,
    updateMutation,
    dragPrefill?.startTime,
    dragPrefill?.originalCoachId,
    dragPrefill?.newCoachId,
    dragPrefill?.newCoachName,
  ]);

  const reservationsFormComponent = useMemo(() => {
    if (isClassDataLoading) {
      return loader;
    }
    if (isClassDataSuccess && classData) {
      return <ReservationsForm kiloClass={classData.data} permission={permission} />;
    }
    return <></>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classData, isClassDataFetching, isClassDataLoading, isClassDataSuccess, loader]);

  const legacyNotes = programData?.data[0]?.workoutNotes ?? null;

  const programmingFormComponent = useMemo(() => {
    if (workoutTrackerEnabled) {
      return <ClassWorkoutView kiloClassId={kiloClassId} legacyNotes={legacyNotes} />;
    }

    const firstProgramData = programData?.data[0];

    if (isProgramDataLoading) {
      return loader;
    }

    if (isProgramDataSuccess && programData) {
      return (
        <>
          <EditProgramForm
            program={firstProgramData}
            permission={permission}
            handleClose={handleProgrammingClose}
            readonly
          />
        </>
      );
    }
    return <></>;
  }, [
    isProgramDataLoading,
    isProgramDataSuccess,
    programData,
    loader,
    permission,
    handleProgrammingClose,
    workoutTrackerEnabled,
    kiloClassId,
    legacyNotes,
  ]);

  const notesComponent = useMemo(() => {
    if (isClassDataLoading) {
      return loader;
    }
    if (isClassDataSuccess && classData) {
      return <NotesView athlete_kilo_classes_or_appointments={classData.data.athleteKiloClasses} />;
    }
    return <></>;
  }, [classData, isClassDataLoading, isClassDataSuccess, loader]);

  const tabs = useMemo(() => {
    const defaultTabs = [
      {
        label: 'Reservations',
        value: 'reservations',
        component: reservationsFormComponent,
        tabProps: { 'data-cy': 'reservationsTab' },
      },
      {
        label: 'Details',
        value: 'details',
        component: detailsComponent,
        tabProps: { 'data-cy': 'detailsTab' },
      },
      {
        label: workoutTrackerEnabled ? 'Workout' : 'Programming',
        value: 'programming',
        component: programmingFormComponent,
        tabProps: { 'data-cy': 'programmingTab' },
      },
      {
        label: 'Notes',
        value: 'notes',
        component: notesComponent,
      },
    ];

    return defaultTabs;
  }, [
    detailsComponent,
    reservationsFormComponent,
    programmingFormComponent,
    notesComponent,
    workoutTrackerEnabled,
  ]);

  const headerData =
    classData &&
    (isDesktop ? (
      <Group sx={{ gap: theme.spacing.xl }}>
        {dayjs(classData.data.startDate).format('MMMM D, YYYY')} at{' '}
        {dayjs(`${classData.data.startDate} ${classData.data.startTime}`).format('h:mm A')} -{' '}
        {calculateEventEndDateTime(
          classData.data.duration,
          classData.data.startTime,
          dayjs(classData.data.startDate).toDate(),
        ).format('h:mm A')}
      </Group>
    ) : (
      <div>
        {dayjs(classData.data.startDate).format('MMMM D, YYYY')}
        <Group>
          {dayjs(`${classData.data.startDate} ${classData.data.startTime}`).format('h:mm A')} -{' '}
          {calculateEventEndDateTime(
            classData.data.duration,
            classData.data.startTime,
            dayjs(classData.data.startDate).toDate(),
          ).format('h:mm A')}
        </Group>
      </div>
    ));

  if (isClassDataError || isProgramError) {
    return (
      <Center p='lg'>
        <Alert icon={<IconExclamationCircle size={px(theme.fontSizes.md)} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      </Center>
    );
  }
  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndStateReset}
      size={670}
      title={<Title order={3}>{classData?.data.kiloClassType.name}</Title>}
      padding='0'
      closeOnClickOutside
      footer={
        <Footer
          state={state}
          setState={setState}
          closeHandler={handleCloseAndStateReset}
          formId={`${state}-class-form`}
          loadingSubmit={updateMutation.isLoading || isClassDataLoading || isClassDataFetching}
        />
      }
      closeButtonSection={
        <Group>
          {headerData}
          <MantineDrawer.CloseButton />
        </Group>
      }
    >
      <LoadingOverlay visible={isClassDataLoading} />

      <MantineTabs value={activeTab} onTabChange={setActiveTab}>
        <MantineTabs.List p='md' pb={0} sx={{ flexWrap: 'nowrap' }}>
          {tabs.map(item => (
            <MantineTabs.Tab {...item.tabProps} key={item.value} value={item.value}>
              {item.label}
            </MantineTabs.Tab>
          ))}

          <Menu position='bottom-end'>
            <Menu.Target>
              <Button
                compact
                disabled={state != 'view' || !permission}
                mt='-.5rem'
                ml='auto'
                data-cy='actionsBtn'
              >
                Actions
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                data-cy='editThisDayOnlyBtn'
                onClick={() => {
                  setState('edit-selected-day');
                  setActiveTab('details');
                }}
              >
                Edit this day only
              </Menu.Item>
              {classData &&
                dayjs(`${classData.data.startDate} ${classData.data.startTime}`).isAfter(
                  dayjs(new Date()),
                ) && (
                  <Menu.Item
                    onClick={() => {
                      setState('edit-all-future-events');
                      setActiveTab('details');
                    }}
                  >
                    Edit all future events
                  </Menu.Item>
                )}
              <Menu.Item onClick={openDeleteModal} data-cy='deleteEventBtn'>
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </MantineTabs.List>

        {tabs.map(item => (
          <MantineTabs.Panel key={item.value} value={item.value} p='xl'>
            {item.component}
          </MantineTabs.Panel>
        ))}
      </MantineTabs>

      <ModalClassDelete
        kiloClassId={kiloClassId}
        opened={deleteModalOpened}
        handleCloseModal={closeDeleteModal}
        handleCloseDrawer={handleCloseAndStateReset}
        setKiloClassId={setKiloClassId}
      />
    </Drawer>
  );
};

export default EditClassDrawer;
